"use client"
import { ProspectoTutorDescuento } from "@/shared/interfaces";
import { gridSizeMdLg12, TipoBeneficioENUM } from "@/shared/constants";
import { Box, Grid, Typography } from "@mui/material";
import { Fragment } from "react";
import { Warning } from "@mui/icons-material";

type EstadoBeneficioCardProps = {
  data: ProspectoTutorDescuento[];
  razonSocial: string;
};


const TIPO_BENEFICIO_MAP: Record<string, string> = {
  es_curador_tutor: TipoBeneficioENUM.FAMILIAR_DISCAPACIDAD,
  es_bono_humano: TipoBeneficioENUM.DESARROLLO_HUMANO,
  es_ayuda_social: TipoBeneficioENUM.AYUDA_SOCIAL,
  es_gremio: TipoBeneficioENUM.APLICA_GREMIO,
  es_reportado: TipoBeneficioENUM.REPORTADO, // score bajo
  reportado: TipoBeneficioENUM.REPORTADO_TELCO,
};

function obtenerColorEstado(estado: string): { bg: string; bar: string; text: string } {
  switch (estado) {
    case 'APROBADO':
      return { bg: '#E8F5E9', bar: '#2E7D32', text: '#1B5E20' };
    case 'RECHAZADO':
      return { bg: '#FFEBEE', bar: '#C62828', text: '#B71C1C' };
    case 'EN ESPERA':
    default:
      return { bg: '#E3F2FD', bar: '#1565C0', text: '#0D47A1' };
  }
}

function obtenerBeneficios(solicitudes: ProspectoTutorDescuento[]): string {

  const datos = solicitudes.map(({ codigo, prospecto }) => {
    const label = Object.entries(prospecto)
      .filter(([key, value]) => value && key in TIPO_BENEFICIO_MAP)
      .map(([key]) => TIPO_BENEFICIO_MAP[key])
      .join(", ");

    return {
      codigo,
      label,
    };
  });

  const conteo = datos.reduce((acc, item) => {
    item.label
      .split(",")
      .map(texto => texto.trim())
      .forEach((texto: string) => {
        acc[texto] = (acc[texto] || 0) + 1;
      });

    return acc;
  }, {} as Record<string, number>);


  const resultado = Object.entries(conteo)
    .map(([label, cantidad]) =>
      cantidad > 1 ? `${label} (${cantidad})` : label
    )
    .join(", ");

  return resultado;
}

export default function EstadoBeneficioCard({ data, razonSocial }: EstadoBeneficioCardProps) {

  const beneficios = obtenerBeneficios(data);
  const scoreBajo = beneficios.includes(TipoBeneficioENUM.REPORTADO);
  const colores = obtenerColorEstado('RECHAZADO');

  return (
    <Grid item {...gridSizeMdLg12}>
      <Fragment key={data[0].codigo}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            borderLeft: `6px solid ${colores.bar}`,
            backgroundColor: colores.bg,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ flex: 1, p: 2 }}>
            <Box sx={{
              display: 'flex',
              gap: 1,
            }}>
              <Warning color="error" />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: colores.text, mb: 0.5, fontSize: '18px' }}
              >
                Advertencia
              </Typography>
            </Box>


            {
              scoreBajo && (
                <>
                  <Typography
                    variant="body2"
                    sx={{ color: colores.bar }}
                  >
                    El prospecto <strong>{razonSocial}</strong> registra un <strong>PRE RECHAZO.</strong>
                  </Typography>

                  <Typography
                    fontStyle="italic"
                    variant="body2"
                    sx={{ color: colores.bar }}
                  >
                    Cliente pre rechazado por Análisis de Crédito. Escalar a su supervisor
                  </Typography>
                </>
              )
            }

            {
              !scoreBajo && (
                <>
                  <Typography
                    variant="body2"
                    sx={{ color: colores.bar }}
                  >
                    El prospecto <strong>{razonSocial}</strong> registra un <strong>BENEFICIO RECHAZADO.</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colores.bar }}
                  >
                    <strong>Beneficio (s) Rechazado (s):</strong> {beneficios}.
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: colores.bar }}
                  >
                    Verifica esta información con su Coordinador antes de continuar con el proceso de registro.
                  </Typography>
                </>
              )
            }
          </Box>
        </Box>
      </Fragment>
    </Grid>
  );
}
